import {TreeViewBaseItem} from '@mui/x-tree-view';
import {
  ArrDatePickerProps,
  ExtendedTreeItemProps,
} from '../../../../shared/interfaces';
import {ISelectItem} from '../../../../shared/types';
import {
  ArrButtonSize,
  ArrButtonVariant,
} from '../../../common/arr-button/ArrButton';

import {GridValueFormatter} from '@mui/x-data-grid';
import {ISavedSearchTreeViewResponse} from '../../../../api/types/savedsearch.types';
import {ArrTextFieldProps} from '../../../common/arr-text-field/ArrTextField';
import AdvancedBatchManagementContainer from '../reports/advanced-batch-management/AdvancedBatchManagementContainer';
import DataExportContainer from '../reports/data-export';
import DocumentAuditContainer from '../reports/document-audit';
import ReviewProgressAssignmentContainer from '../reports/review-progress-assignment/ReviewProgressAssignmentContainer';
import ReviewProgressReviewerContainer from '../reports/review-progress-reviewer/ReviewProgressReviewerContainer';
import ReviewResultsContainer from '../reports/review-results';
import ReviewerProductivityContainer from '../reports/reviewer-productivity/ReviewerProductivityContainer';
import {ReviewerTimeOnlineContainer} from '../reports/reviewer-time-online/ReviewerTimeOnlineContainer';
import SearchTermReportContainer from '../reports/search-term/SearchTermReportContainer';
import TagCountsByTagReportContainer from '../reports/tagByCountTagReport/TagCountsByTagReportContainer';
import TimelineContainer from '../reports/timeline-and-gaps/TimelineContainer';
import UserLoginActivityContainer from '../reports/user-login-activity';
import ReviewersCountContainer from '../reports/reviewers-count/ReviewersCountContainer';
import UserActivityContainer from '../reports/User-Activity';

export const reportTypeMap: Record<string, React.FC> = {
  UserLoginActivityReport: UserLoginActivityContainer,
  ReviewResults: ReviewResultsContainer,
  DocumentAudit: DocumentAuditContainer,
  UserActivity: UserActivityContainer,
  'document-audit': DocumentAuditContainer,
  'user-activity': UserActivityContainer,
  ExportData: DataExportContainer,
  ReviewerCountsReport: ReviewersCountContainer,
  'reviewer-counts': ReviewersCountContainer,
  SearchTermReport: SearchTermReportContainer,
  AdvancedBatchManagementReport: AdvancedBatchManagementContainer,
  'batch-report': AdvancedBatchManagementContainer,
  AssignmentProgressReport: ReviewProgressAssignmentContainer,
  'assign-report': ReviewProgressAssignmentContainer,
  Timeline: TimelineContainer,
  timeline: TimelineContainer,
  term: SearchTermReportContainer,
  'user-login-activity': UserLoginActivityContainer,
  ReviewerTimeOnline: ReviewerTimeOnlineContainer,
  TagcountsByTagReport: TagCountsByTagReportContainer,
  'tag-counts': TagCountsByTagReportContainer,
  'review-results': ReviewResultsContainer,
  ReviewerProductivityReport: ReviewerProductivityContainer,
  'reviewer-time-online': ReviewerTimeOnlineContainer,
  ReviewerProgressReport: ReviewProgressReviewerContainer,
  'progress-report': ReviewProgressReviewerContainer,
  'reviewer-productivity': ReviewerProductivityContainer,
};

export interface ReportVariant {
  key: string;
  jsonConfigPath: string;
}

export interface ReportRegistryItem {
  title: string;
  showInfoIcon?: boolean;
  variants: ReportVariant[];
  dynamicDataLoader?: () => Promise<any>;
}

// Action types
export type ActionType = 'button';
export interface Action {
  input_type: ActionType;
  label: string;
  actionKey: string;
  variant: ArrButtonVariant;
  size: ArrButtonSize;
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
  'data-testid'?: string;
}

// Field types for Selection Criteria
export type FieldType = 'textField' | 'dropdown' | 'radioGroup' | 'toggle'; // explicit literals;
export interface Field {
  input_type: FieldType;
  label: string;
  showInfoIcon: boolean;
  tooltipText: string;
  key: string;
  required?: boolean;
  size?: 'small' | 'medium' | 'large';
  multiSelect?: boolean;
  options?: {value: string; label: string}[];
  value?: any;
  toggleSize?: 'large' | 'medium' | 'small' | 'xs';
  labelText?: string;
}

// Filter types for dynamic sections
export type FilterType =
  | 'button'
  | 'dropdown'
  | 'multiSelectTreeViewList'
  | 'datePicker'
  | 'checkboxList'
  | 'checkbox'
  | 'toggle'
  | 'exportFormatMenu'
  | 'arrSourceSelectedSection'
  | 'multiSelectTreeViewList'
  | 'input';

// Base props for all components
export interface BaseComponentProps {
  label?: string;
  key: string;
  value?: any;
  onChange?: (val: any, key?: any) => void;
  options?: any[];
  name?: string;
  size?: 'large' | 'medium' | 'small' | 'xs';
  initialValues?: any;
}

// Component-specific props
export interface DatePickerProps extends BaseComponentProps {
  input_type: 'datePicker';
  placeHolder?: string;
  inputClass?: string;
  initialStartDate?: string;
  initialEndDate?: string;
  mode?: 'date' | 'time' | 'date-time';
  maxDateTime?: ArrDatePickerProps['maxDateTime'];
  minDateTime?: ArrDatePickerProps['minDateTime'];
  triggerOnChangeOnInitialChange?: boolean;
  dataTestId?: string;
}

export interface TextFieldProps {
  /** Unique identifier for the input */
  id?: string;

  /** Name attribute for form submission */
  name?: string;

  /** Current value of the input */
  value: string;

  /** Placeholder text shown when empty */
  placeholder?: string;

  /** Label displayed above or beside the input */
  label?: string;

  /** Input type (text, password, email, number, etc.) */
  type?: 'text' | 'password' | 'email' | 'number' | 'search' | 'tel' | 'url';

  /** Whether the field is disabled */
  disabled?: boolean;

  /** Whether the field is required */
  required?: boolean;

  /** Optional max length of input */
  maxLength?: number;

  /** Optional min length of input */
  minLength?: number;

  /** Callback when value changes */
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;

  /** Callback when input loses focus */
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;

  /** Callback when input gains focus */
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;

  /** Custom CSS class */
  className?: string;

  /** Inline styles */
  style?: React.CSSProperties;

  /** Error message to display */
  errorMessage?: string;

  /** Whether to show input as read-only */
  readOnly?: boolean;
  initialValues?: any;
}

export interface DropdownProps extends BaseComponentProps {
  input_type: 'dropdown';
  options?: string[];
  size?: 'small' | 'medium' | 'large';
  dropdownItems?: ISelectItem[];
  searchable?: true;
}

export interface CheckboxListProps extends BaseComponentProps {
  showSelectAll?: boolean;
  input_type: 'checkboxList';
  childLabels: {label: string; value: boolean}[];
  size?: 'xs' | 'small' | 'medium' | 'large';
}

export interface CheckboxProps extends BaseComponentProps {
  input_type: 'checkbox';
  labelText?: string;
  size?: 'xs' | 'small' | 'medium' | 'large';
  disabled?: boolean;
  checked?: boolean;
  borderRadius?: number;
}

export interface ToggleProps extends BaseComponentProps {
  input_type: 'toggle';
  labelText?: string;
  toggleSize?: 'large' | 'medium' | 'small' | 'xs';
  disabled?: boolean;
  value?: boolean;
  ariaLabel?: string;
  dataTestId?: string;
}
export interface RadioGroupProps extends BaseComponentProps {
  value: string;
  options: {value: string; label: string; tooltip?: string}[];
  name?: string;
  size?: 'large' | 'medium' | 'small' | 'xs';
  label?: string;
  disabled?: boolean;
  className?: string;
  row?: boolean;
  [key: string]: any;
}

export interface ButtonProps extends BaseComponentProps {
  input_type: 'button';
  variant: ArrButtonVariant;
  size: ArrButtonSize;
  disabled?: boolean;
  className?: string;
  type?: 'submit' | 'reset';
  onClick?: () => void;
  'data-testid'?: string;
}

// props for ArrSourceSelectedSection
export interface ArrSourceSelectedSectionProps extends BaseComponentProps {
  input_type: 'arrSourceSelectedSection';
  sources: any[];
}

export interface MultiSelectDropdownProps extends BaseComponentProps {
  input_type: 'multiselectdropdown';
  options?: ISelectItem[];
  size?: 'small' | 'medium' | 'large';
  searchable?: true;
}
export interface MultiSelectTreeViewListListProps extends BaseComponentProps {
  input_type: 'multiSelectTreeViewList';
  treeData: TreeViewBaseItem<ExtendedTreeItemProps>[];
  defaultExpandedIds?: string[];
}
export interface TextFieldProps extends BaseComponentProps {
  input_type: 'textField';
  placeholder?: string;
  inputClass?: string;
  overrideArrProps?: ArrTextFieldProps;
}

// Union type for all filter components
export type FilterComponent =
  | DatePickerProps
  | DropdownProps
  | MultiSelectDropdownProps
  | CheckboxListProps
  | CheckboxProps
  | ToggleProps
  | RadioGroupProps
  | ButtonProps
  | ExportFormatMenuProps
  | ArrSourceSelectedSectionProps
  | MultiSelectTreeViewListListProps
  | TextFieldProps;

// Accordion section type
export interface AccordionSection {
  id: string;
  title: string;
  showInfoIcon?: boolean;
  tooltipText?: string;
  tooltipTitle?: string;
  components: FilterComponent[];
  showAccordion?: boolean;
  isRequired?: boolean;
  className?: string;
  sectionClassName?: string;
  isDefaultExpand?: boolean;
}

// Section structure
export interface Section {
  tooltipText: string | undefined;
  tooltipTitle?: string;
  title: string;
  showInfoIcon?: boolean;
  filters: AccordionSection[];
  isRequired?: boolean;
}

// Selection Criteria structure
export interface SelectionCriteria {
  title: string;
  actions: Action[];
  fields: Field[];
  showInfoIcon?: boolean;
  tooltipText?: string;
  tooltipTitle?: string;
  forceInLineToolTip?: boolean;
  isRequired?: boolean;
}

// Root config structure
export interface Config {
  selectionCriteria: SelectionCriteria;
  sections: Section[];
}

export interface Reports {
  config: Config;
  title: string;
  showInfoIcon?: boolean;
  infoContent?: string[] | string;
  infoModalTitle?: string;
}

export interface RightPanelConfig {
  showUploadIcon?: boolean;
  showSaveIcon?: boolean;
  showColumnsIcon?: boolean;
  showActionButton?: boolean;
  title?: string;
  subTitle?: string;
  onUploadClick?: () => void;
  saveReportType?: 'default' | 'timelineGap';
}

export interface ColumnConfig {
  field: string;
  headerName: string;
  isVisible: boolean;
  flex?: number;
  minWidth?: number;
  index?: number;
  renderCell?: (params: any) => React.ReactNode;
  width?: number;
  cellClassName?: string;
  headerClassName?: string;
  valueFormatter?: GridValueFormatter;
}
export interface ExportFormatMenuProps extends BaseComponentProps {
  label: string;
  dropdownItem: [];
  selectedValue: ISelectItem | null;
  input_type: 'exportFormatMenu';
}
export type TableRowData = {
  tag: string;
  count: string;
  count1: string;
  count2: string;
  count3: string;
  count4: string;
  count5: string;
  count6: string;
  uniqueCount: string;
  selected: {[key: string]: boolean};
};

export interface IUserLoginActivitySavePayload {
  reportTypeCode: string;
  selectedReviewers: number[];
  fromDateTime: string | null;
  toDateTime: string | null;
  showCurrentlyLoggedInUsers: number;
  selectedCustomColumns: number[];
  selectedSource: string;
  pageNumber: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  reportId: number;
  reportID?: number;
  searchData?: never;
  selected_SearchID?: never;
  updated_search_ID?: never;
}
export interface ISearchTermSavePayload {
  searchTermReportType: string;
  selected_SearchID: string[];
  pageNumber: number;
  pageSize: number;
  selectedCustomColumns: number[];
  selectedCustomColumnName: string[];
  isShared: boolean;
  isSavedSearchSource: boolean;
  reportId: number;
  reportTypeCode: string;
  reportID?: number;
  searchData?: ISavedSearchTreeViewResponse[];
  updated_search_ID?: string[];
}
